import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import { ApiError } from '@/services/httpClient';
import { queryKeys } from '@/lib/queryKeys';
import type { Application } from '@/types';
import {
  isApplicationPipelineRecentlyInFlight,
  isApplicationProcessingStuckDisplay,
  isApplicationStuckProcessing,
  isApplicationAbortableProcessing,
  isApplicationExplicitStopEligible,
  countExplicitStopEligible,
} from '@/lib/pendingProcessing';
import {
  BULK_PROCESS_BATCH_SIZE,
  runBulkAbortStuck as executeBulkAbortStuck,
  runBulkProcessNowBatched,
} from '@/lib/bulkProcessApplications';
import {
  clearBulkProcessQueue,
  loadBulkProcessQueue,
  mergeBulkTargetIds,
  mergeBulkTargetRefs,
  saveBulkProcessQueue,
} from '@/lib/bulkProcessQueuePersistence';
import {
  isApplicationAwaitingScreeningSlot,
} from '@/lib/applicationScreening';
import {
  coalesceInflightApplicationStates,
  countBulkBannerCompleted,
  expandProcessingIdsForBulkTargets,
  hydrateApplicationsFromDetail,
  isBulkEnqueueListLagState,
  reconcileQueueIds,
  shouldClearProcessingIndicator,
  shouldReconcileClearProcessingId,
} from '@/lib/bulkProcessQueueSync';
import { mergeApplicationsById } from '@/lib/bulkProcessApplications';
import {
  isApplicationBulkBatchInFlight,
  isBulkBatchTargetId,
  isBulkBatchRowWaitingForSlot,
  resolveBulkTargetIds,
} from '@/lib/bulkProcessQueueState';
import { syncBulkQueueProgress } from '@/lib/syncBulkQueueProgress';
import { BULK_QUEUE_POLL_MIN_MS } from '@/lib/bulkQueuePoll';
import { useBulkProcessBannerStore } from '@/stores/bulkProcessBannerStore';
import { toast } from 'sonner';
import { emitBillingUsageChanged } from '@/lib/billingEvents';

export {
  shouldClearProcessingIndicator,
  shouldClearStoppingIndicator,
  coalesceInflightApplicationStates,
  activeBulkBatchIdSet,
} from '@/lib/bulkProcessQueueSync';

export type BulkProcessResult = {
  processed: number;
  skippedBilling: number;
  errors: number;
  total: number;
};

function toBulkItems(apps: Application[]): Array<{ applicationId: string; positionId: string }> {
  return apps.map(app => ({
    applicationId: app.id,
    positionId: app.positionId,
  }));
}

function patchByIds(
  prev: Application[],
  ids: Set<string>,
  patch: Partial<Application>,
): Application[] {
  if (ids.size === 0) return prev;
  return prev.map(a => (ids.has(a.id) ? { ...a, ...patch } : a));
}

/**
 * Per-application processing UX: optimistic state + queue badges update synchronously
 * on confirm; API runs in background. Toolbar buttons stay enabled.
 */
export function useApplicationProcessQueue(options: {
  companyId: string | undefined;
  applications: Application[];
  refetchApplications: () => Promise<unknown>;
  onPatchApplications?: (updater: (prev: Application[]) => Application[]) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
}) {
  const { companyId, applications, refetchApplications, onPatchApplications, t } = options;
  const queryClient = useQueryClient();
  const [stoppingIds, setStoppingIds] = useState<Set<string>>(new Set());
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());
  const runIdRef = useRef(0);
  const hydratedCompanyRef = useRef<string | null>(null);
  const bulkTargetIdsRef = useRef<string[]>([]);
  const bulkPhase = useBulkProcessBannerStore((s) => s.phase);
  const prevBulkPhaseRef = useRef(bulkPhase);
  const startBulkBannerRun = useBulkProcessBannerStore(s => s.startBulkRun);
  const syncBulkBannerProgress = useBulkProcessBannerStore(s => s.syncProgress);
  const clearBulkBannerRun = useBulkProcessBannerStore(s => s.clearBulkRun);

  const readApplicationsCache = useCallback((): Application[] => {
    if (!companyId) return applications;
    const legacy =
      queryClient.getQueryData<Application[]>(queryKeys.applications(companyId, false));
    if (legacy?.length) return legacy;
    return applications;
  }, [companyId, applications, queryClient]);

  // Resume queue + banner after navigation (e.g. back from application detail).
  useEffect(() => {
    if (!companyId || hydratedCompanyRef.current === companyId) return;
    hydratedCompanyRef.current = companyId;
    const loaded = loadBulkProcessQueue(companyId);
    if (
      loaded.stoppingIds.size === 0
      && loaded.processingIds.size === 0
      && loaded.targetIds.length === 0
    ) {
      return;
    }

    if (loaded.targetIds.length > 0) {
      bulkTargetIdsRef.current = loaded.targetIds;
      const expandedProcessing = expandProcessingIdsForBulkTargets(
        loaded.targetIds,
        loaded.processingIds,
        applications,
        { includeUnknownTargets: applications.length > 0 },
      );
      const settled = countBulkBannerCompleted(
        loaded.targetIds,
        applications,
        expandedProcessing,
        loaded.stoppingIds,
        new Set(loaded.targetIds),
      );
      const initialCompleted = Math.max(loaded.completed, settled);
      if (initialCompleted < loaded.targetIds.length) {
        startBulkBannerRun(companyId, loaded.targetIds, {
          resume: true,
          initialCompleted,
        });
      }
      setStoppingIds(loaded.stoppingIds);
      setProcessingIds(expandedProcessing);
    } else {
      setStoppingIds(loaded.stoppingIds);
      setProcessingIds(loaded.processingIds);
    }

    void syncBulkQueueProgress(queryClient, companyId, syncBulkBannerProgress, {
      targetIds: loaded.targetIds,
      forceListRefetch: loaded.targetIds.length > 0,
      resumeHydrate: loaded.targetIds.length > 0,
    }).then(() => {
      const fresh = readApplicationsCache();
      const after = loadBulkProcessQueue(companyId);
      const expandedAfter = expandProcessingIdsForBulkTargets(
        after.targetIds,
        after.processingIds,
        fresh,
        { includeUnknownTargets: fresh.length > 0 },
      );
      bulkTargetIdsRef.current = after.targetIds;
      setStoppingIds(after.stoppingIds);
      setProcessingIds(expandedAfter);
      onPatchApplications?.((prev) => mergeApplicationsById(prev, fresh));
    });
  }, [
    companyId,
    applications,
    queryClient,
    syncBulkBannerProgress,
    startBulkBannerRun,
    readApplicationsCache,
    onPatchApplications,
  ]);

  useEffect(() => {
    if (!companyId) return;
    const resolvedTargets = resolveBulkTargetIds(companyId, bulkTargetIdsRef.current);
    if (resolvedTargets.length > 0) {
      bulkTargetIdsRef.current = resolvedTargets;
    }
    if (
      resolvedTargets.length === 0
      && processingIds.size === 0
      && stoppingIds.size === 0
    ) {
      const existing = loadBulkProcessQueue(companyId);
      if (existing.targetIds.length > 0) return;
    }
    saveBulkProcessQueue(
      companyId,
      stoppingIds,
      processingIds,
      resolvedTargets,
      undefined,
      mergeBulkTargetRefs(
        loadBulkProcessQueue(companyId).targetRefs,
        applications,
        resolvedTargets,
      ),
    );
  }, [companyId, stoppingIds, processingIds, applications]);

  // Keep banner "N of K" in sync when list data arrives or queue ids reconcile locally.
  useEffect(() => {
    if (!companyId) return;
    const banner = useBulkProcessBannerStore.getState();
    if (banner.phase !== 'active' || banner.companyId !== companyId) return;
    if (applications.length === 0) return;

    const bulkTargetIds = bulkTargetIdsRef.current.length > 0
      ? new Set(bulkTargetIdsRef.current)
      : new Set(banner.targetIds);
    const { stoppingIds: nextStopping, processingIds: nextProcessing } = reconcileQueueIds(
      applications,
      stoppingIds,
      processingIds,
      { bulkTargetIds },
    );
    syncBulkBannerProgress(companyId, nextProcessing, nextStopping, applications);
  }, [
    companyId,
    applications,
    bulkPhase,
    stoppingIds,
    processingIds,
    syncBulkBannerProgress,
  ]);

  // Share queue with other routes (e.g. application detail) via session + banner store.
  useEffect(() => {
    if (!companyId) return;
    const banner = useBulkProcessBannerStore.getState();
    if (banner.phase !== 'active' || banner.companyId !== companyId) return;
    const loaded = loadBulkProcessQueue(companyId);
    if (loaded.processingIds.size === 0 && loaded.stoppingIds.size === 0) return;
    bulkTargetIdsRef.current = loaded.targetIds;
    setProcessingIds(loaded.processingIds);
    setStoppingIds(loaded.stoppingIds);
  }, [companyId, bulkPhase]);

  useEffect(() => {
    if (!companyId) return;
    if (processingIds.size > 0 || stoppingIds.size > 0) return;
    const banner = useBulkProcessBannerStore.getState();
    if (banner.phase === 'active' && banner.companyId === companyId) return;
    const loaded = loadBulkProcessQueue(companyId);
    if (loaded.targetIds.length > 0) return;
    bulkTargetIdsRef.current = [];
  }, [companyId, processingIds.size, stoppingIds.size]);

  const isStopping = useCallback((id: string) => stoppingIds.has(id), [stoppingIds]);
  const isProcessing = useCallback(
    (id: string) => {
      if (!processingIds.has(id)) return false;
      const app = applications.find((a) => a.id === id);
      if (!app) return true;
      const bulkTargetIds = bulkTargetIdsRef.current;
      const bulkActive = useBulkProcessBannerStore.getState().phase === 'active';
      if (
        bulkActive
        && bulkTargetIds.includes(id)
        && isBulkEnqueueListLagState(app.state)
      ) {
        return true;
      }
      if (shouldClearProcessingIndicator(app.state)) return false;
      return true;
    },
    [processingIds, applications],
  );

  /** Processing tab — all in-flight bulk rows (queue + batch scope; survives reconcile clearing ids). */
  const isInProcessingFilter = useCallback(
    (id: string) => {
      const app = applications.find((a) => a.id === id);
      if (app && isApplicationPipelineRecentlyInFlight(app)) return true;
      return isApplicationBulkBatchInFlight(
        app ?? { id, state: 'APPLIED_NEW' },
        companyId,
        processingIds,
        stoppingIds,
      );
    },
    [applications, companyId, processingIds, stoppingIds],
  );

  /**
   * Status badge — active slot (PIPELINE_RUNNING / recently in flight), not queued bulk rows.
   */
  const showsActiveProcessingBadge = useCallback(
    (id: string) => {
      const app = applications.find((a) => a.id === id);
      const inBulkTarget = isBulkBatchTargetId(companyId, id);

      if (isBulkBatchRowWaitingForSlot(app, inBulkTarget)) {
        return false;
      }

      if (processingIds.has(id)) {
        if (!app) return true;
        if (isBulkBatchRowWaitingForSlot(app, inBulkTarget)) return false;
        if (shouldClearProcessingIndicator(app.state)) return false;
        return true;
      }
      if (app && isApplicationPipelineRecentlyInFlight(app)) {
        if (shouldClearProcessingIndicator(app.state)) return false;
        return true;
      }
      if (inBulkTarget && app?.state === 'PIPELINE_RUNNING') {
        return true;
      }
      return false;
    },
    [companyId, processingIds, applications],
  );

  /** Bulk batch row waiting for a pipeline slot (APPLIED_NEW / READY_TO_PROCESS in active batch). */
  const showsQueuedInBulkBadge = useCallback(
    (id: string) => {
      if (stoppingIds.has(id)) return false;
      const inBulkTarget = isBulkBatchTargetId(companyId, id);
      const app = applications.find((a) => a.id === id);
      if (!inBulkTarget && app && isApplicationAwaitingScreeningSlot(app)) {
        return true;
      }
      if (!inBulkTarget) return false;
      if (app?.state === 'PIPELINE_RUNNING') return false;
      if (app && isApplicationPipelineRecentlyInFlight(app)) return false;
      if (isBulkBatchRowWaitingForSlot(app, true)) return true;
      if (app && shouldClearProcessingIndicator(app.state)) return false;
      return processingIds.has(id);
    },
    [companyId, applications, stoppingIds, processingIds],
  );

  const isRowInUiProcessingQueue = useCallback(
    (id: string) => processingIds.has(id) || stoppingIds.has(id),
    [processingIds, stoppingIds],
  );

  const isExplicitStopEligible = useCallback(
    (id: string) => {
      const app = applications.find((a) => a.id === id);
      if (!app) return isRowInUiProcessingQueue(id);
      return isApplicationExplicitStopEligible(
        app,
        isRowInUiProcessingQueue(id) || showsActiveProcessingBadge(id),
      );
    },
    [applications, isRowInUiProcessingQueue, showsActiveProcessingBadge],
  );

  const countSelectedStopEligible = useCallback(
    (apps: Application[]) => countExplicitStopEligible(apps, isExplicitStopEligible),
    [isExplicitStopEligible],
  );

  const isRowInPipelineUi = useCallback(
    (id: string) =>
      stoppingIds.has(id)
      || showsActiveProcessingBadge(id)
      || showsQueuedInBulkBadge(id),
    [stoppingIds, showsActiveProcessingBadge, showsQueuedInBulkBadge],
  );

  /** Processing stuck — only after grace window, not while row is in bulk/UI queue. */
  const showsProcessingStuckBadge = useCallback(
    (id: string) => {
      const app = applications.find((a) => a.id === id);
      if (!app) return false;
      return isApplicationProcessingStuckDisplay(app, isRowInPipelineUi(id));
    },
    [applications, isRowInPipelineUi],
  );

  const filterStuckForAbort = useCallback(
    (apps: Application[]) =>
      apps.filter((a) => isApplicationStuckProcessing(a, isRowInPipelineUi(a.id))),
    [isRowInPipelineUi],
  );

  const applyQueueToCache = useCallback((
    fresh: Application[],
    nextProcessing: Set<string>,
  ) => {
    if (!onPatchApplications) return;
    if (nextProcessing.size === 0) {
      onPatchApplications((prev) => {
        const byId = new Map(fresh.map((a) => [a.id, a]));
        return prev.map((a) => byId.get(a.id) ?? a);
      });
      return;
    }
    onPatchApplications((prev) => {
      const merged = prev.map((a) => fresh.find((f) => f.id === a.id) ?? a);
      return coalesceInflightApplicationStates(merged, nextProcessing);
    });
  }, [onPatchApplications]);

  // Drop queue indicators once server state has caught up (e.g. after navigation back from detail).
  useEffect(() => {
    if (stoppingIds.size === 0 && processingIds.size === 0) return;
    const bulkTargetIds = bulkTargetIdsRef.current.length > 0
      ? new Set(bulkTargetIdsRef.current)
      : undefined;
    const { stoppingIds: nextStopping, processingIds: nextProcessing } = reconcileQueueIds(
      applications,
      stoppingIds,
      processingIds,
      { bulkTargetIds },
    );
    if (nextStopping.size !== stoppingIds.size) setStoppingIds(nextStopping);
    if (nextProcessing.size !== processingIds.size) setProcessingIds(nextProcessing);
    applyQueueToCache(applications, nextProcessing);
  }, [applications, stoppingIds, processingIds, applyQueueToCache]);

  const refetchWithInflightCoalesce = useCallback(async () => {
    if (!companyId) return;
    const bulkActive = useBulkProcessBannerStore.getState().phase === 'active';
    if (bulkActive) {
      await syncBulkQueueProgress(queryClient, companyId, syncBulkBannerProgress, {
        targetIds: bulkTargetIdsRef.current,
      });
      const loaded = loadBulkProcessQueue(companyId);
      setStoppingIds(loaded.stoppingIds);
      setProcessingIds(loaded.processingIds);
      return;
    }
    await refetchApplications();
    const fresh = readApplicationsCache();
    const bulkTargetIds = bulkTargetIdsRef.current.length > 0
      ? new Set(bulkTargetIdsRef.current)
      : undefined;
    const { stoppingIds: nextStopping, processingIds: nextProcessing } = reconcileQueueIds(
      fresh,
      stoppingIds,
      processingIds,
      { bulkTargetIds },
    );
    if (nextStopping.size !== stoppingIds.size) setStoppingIds(nextStopping);
    if (nextProcessing.size !== processingIds.size) setProcessingIds(nextProcessing);
    applyQueueToCache(fresh, nextProcessing);
  }, [
    companyId,
    queryClient,
    syncBulkBannerProgress,
    refetchApplications,
    readApplicationsCache,
    stoppingIds,
    processingIds,
    applyQueueToCache,
  ]);

  // Bulk banner finished — clear queue badges and sync list with server truth.
  useEffect(() => {
    const wasActive = prevBulkPhaseRef.current === 'active';
    prevBulkPhaseRef.current = bulkPhase;
    if (wasActive && bulkPhase === 'idle' && companyId) {
      bulkTargetIdsRef.current = [];
      setProcessingIds(new Set());
      setStoppingIds(new Set());
      void refetchWithInflightCoalesce();
    }
  }, [bulkPhase, companyId, refetchWithInflightCoalesce]);

  // While bulk banner is active, AppLayout runs adaptive syncBulkQueueProgress.
  useEffect(() => {
    if (!companyId) return;
    if (bulkPhase === 'active') return;
    if (stoppingIds.size === 0 && processingIds.size === 0) return;

    const interval = setInterval(() => {
      void refetchWithInflightCoalesce();
    }, BULK_QUEUE_POLL_MIN_MS);

    return () => clearInterval(interval);
  }, [
    companyId,
    bulkPhase,
    stoppingIds.size,
    processingIds.size,
    refetchWithInflightCoalesce,
  ]);

  const markBulkStopIds = useCallback((ids: readonly string[]) => {
    if (ids.length === 0) return;
    const idSet = new Set(ids);
    setStoppingIds(prev => {
      const next = new Set(prev);
      for (const id of idSet) next.add(id);
      return next;
    });
    setProcessingIds(prev => {
      const next = new Set(prev);
      for (const id of idSet) next.delete(id);
      return next;
    });
  }, []);

  /** Synchronous — row "Stopping…" badge; server state updates after bulk-abort returns. */
  const markBulkStop = useCallback((apps: Application[], options?: { explicit?: boolean }) => {
    const ids = options?.explicit
      ? apps.map((a) => a.id)
      : filterStuckForAbort(apps).map((a) => a.id);
    markBulkStopIds(ids);
  }, [filterStuckForAbort, markBulkStopIds]);

  /** Synchronous — optimistic queue badge for every targeted row. */
  const markBulkProcess = useCallback((
    apps: Application[],
    options?: { replace?: boolean },
  ) => {
    if (apps.length === 0) return;
    const allIds = new Set(apps.map(a => a.id));
    const stuckIds = new Set(filterStuckForAbort(apps).map(a => a.id));

    setProcessingIds(prev => {
      const next = options?.replace ? new Set<string>() : new Set(prev);
      for (const id of allIds) next.add(id);
      return next;
    });

    if (options?.replace) {
      setStoppingIds(stuckIds);
    } else if (stuckIds.size > 0) {
      setStoppingIds(prev => {
        const next = new Set(prev);
        for (const id of stuckIds) next.add(id);
        return next;
      });
    }
  }, [filterStuckForAbort]);

  const clearQueueForIds = useCallback((ids: string[]) => {
    const idSet = new Set(ids);
    setStoppingIds(prev => {
      const next = new Set(prev);
      for (const id of idSet) next.delete(id);
      return next;
    });
    setProcessingIds(prev => {
      const next = new Set(prev);
      for (const id of idSet) next.delete(id);
      return next;
    });
  }, []);

  const runBulkAbortStuck = useCallback(
    async (
      apps: Application[],
      options?: { mode?: 'stuck' | 'explicit' },
    ): Promise<{ aborted: number; errors: number } | null> => {
      if (!companyId) return null;

      const explicit = options?.mode === 'explicit';
      let workingApps = apps;
      if (explicit && apps.length > 0) {
        const persisted = loadBulkProcessQueue(companyId);
        const mergedApps = mergeApplicationsById(readApplicationsCache(), apps);
        workingApps = await hydrateApplicationsFromDetail(
          companyId,
          mergedApps,
          apps.map((a) => a.id),
          mergeBulkTargetRefs(persisted.targetRefs, mergedApps, apps.map((a) => a.id)),
        );
        onPatchApplications?.((prev) => mergeApplicationsById(prev, workingApps));
      }

      const stuck = explicit
        ? workingApps.filter((a) => isApplicationAbortableProcessing(a))
        : filterStuckForAbort(workingApps);
      const selectedIds = apps.map((a) => a.id);

      if (explicit && selectedIds.length > 0 && stuck.length === 0) {
        markBulkStopIds(selectedIds);
        clearQueueForIds(selectedIds);
        onPatchApplications?.((prev) => {
          const byId = new Map(workingApps.map((a) => [a.id, a]));
          return prev.map((a) => byId.get(a.id) ?? a);
        });
        syncBulkBannerProgress(
          companyId,
          new Set([...processingIds].filter((id) => !selectedIds.includes(id))),
          stoppingIds,
          workingApps,
        );
        await refetchWithInflightCoalesce();
        toast.info(t('applications.bulkStopClearedStale', { count: selectedIds.length }));
        return { aborted: 0, errors: 0 };
      }

      if (stuck.length === 0) return null;

      const runId = ++runIdRef.current;
      const stuckIds = stuck.map(a => a.id);
      markBulkStop(stuck, { explicit });

      try {
        const result = await executeBulkAbortStuck(companyId, stuck);
        if (runId !== runIdRef.current) return null;

        if (result.aborted > 0) {
          toast.success(t('applications.bulkStopSuccess', { count: result.aborted }));
        }
        if (result.errors > 0) {
          toast.error(t('applications.bulkStopPartial', { count: result.errors }));
        }
        const staleOnly = explicit && result.skipped > 0 && result.aborted === 0 && result.errors === 0;
        if (staleOnly) {
          toast.info(t('applications.bulkStopClearedStale', { count: result.skipped }));
        } else if (result.skipped > 0 && result.aborted === 0 && result.errors === 0) {
          toast.info(t('applications.bulkStopSkipped', { count: result.skipped }));
        }

        if (explicit) {
          clearQueueForIds(selectedIds);
        }

        if (result.aborted > 0) {
          const stopped = new Set(stuckIds);
          onPatchApplications?.(prev =>
            patchByIds(prev, stopped, { state: 'READY_TO_PROCESS' }),
          );
        } else if (explicit) {
          onPatchApplications?.((prev) => {
            const byId = new Map(workingApps.map((a) => [a.id, a]));
            return prev.map((a) => byId.get(a.id) ?? a);
          });
        }

        setProcessingIds(prev => {
          const next = new Set(prev);
          for (const id of stuckIds) next.delete(id);
          if (explicit) {
            for (const id of selectedIds) next.delete(id);
          }
          return next.size === prev.size ? prev : next;
        });
        await refetchWithInflightCoalesce();
        return { aborted: result.aborted, errors: result.errors };
      } catch (err: unknown) {
        const msg = err instanceof ApiError && err.status === 404
          ? t('applications.bulkStopApiUnavailable')
          : (err as { message?: string })?.message ?? 'Stop processing failed';
        toast.error(msg);
        clearQueueForIds(explicit ? selectedIds : stuckIds);
        await refetchWithInflightCoalesce();
        return null;
      }
    },
    [
      companyId,
      markBulkStop,
      markBulkStopIds,
      onPatchApplications,
      refetchWithInflightCoalesce,
      clearQueueForIds,
      readApplicationsCache,
      processingIds,
      stoppingIds,
      syncBulkBannerProgress,
      t,
    ],
  );

  /** Sync — queue badges + banner before filter switches to Processing (avoids empty list). */
  const prepareBulkProcess = useCallback((apps: Application[]) => {
    if (!companyId || apps.length === 0) return;
    const newIds = apps.map(a => a.id);
    const banner = useBulkProcessBannerStore.getState();
    const merging = banner.phase === 'active' && banner.companyId === companyId;
    const mergedTargetIds = merging
      ? mergeBulkTargetIds(banner.targetIds, newIds)
      : newIds;

    bulkTargetIdsRef.current = mergedTargetIds;

    const stuckIds = new Set(filterStuckForAbort(apps).map(a => a.id));
    const persisted = loadBulkProcessQueue(companyId);
    const nextProcessing = merging
      ? new Set([...persisted.processingIds, ...newIds])
      : new Set(newIds);

    if (merging) {
      startBulkBannerRun(companyId, newIds, { merge: true });
      markBulkProcess(apps, { replace: false });
    } else {
      startBulkBannerRun(companyId, mergedTargetIds);
      markBulkProcess(apps, { replace: true });
    }

    saveBulkProcessQueue(
      companyId,
      merging
        ? new Set([...persisted.stoppingIds, ...stuckIds])
        : stuckIds,
      nextProcessing,
      mergedTargetIds,
      merging ? Math.max(persisted.completed, banner.completed) : undefined,
      mergeBulkTargetRefs(persisted.targetRefs, apps, mergedTargetIds),
    );
    applyQueueToCache(applications, nextProcessing);
  }, [
    companyId,
    applications,
    markBulkProcess,
    startBulkBannerRun,
    filterStuckForAbort,
    applyQueueToCache,
  ]);

  const runBulkProcess = useCallback(
    async (
      apps: Application[],
      options?: { skipPrepare?: boolean },
    ): Promise<BulkProcessResult | null> => {
      if (!companyId || apps.length === 0) return null;

      const runId = ++runIdRef.current;
      const allIds = apps.map(a => a.id);
      const stuck = filterStuckForAbort(apps);

      if (!options?.skipPrepare) {
        prepareBulkProcess(apps);
      }

      try {
        if (stuck.length > 0) {
          const abortResult = await executeBulkAbortStuck(companyId, stuck);
          if (runId !== runIdRef.current) return null;
          if (abortResult?.aborted) {
            onPatchApplications?.(prev =>
              patchByIds(prev, new Set(stuck.map(a => a.id)), { state: 'READY_TO_PROCESS' }),
            );
          }
          setStoppingIds(new Set());
        }

        const result = await runBulkProcessNowBatched(companyId, apps, {
          onBatchComplete: (done, total) => {
            if (total > BULK_PROCESS_BATCH_SIZE && done < total) {
              toast.loading(t('application.pendingBilling.bulkProcessing', { count: total }), {
                id: 'bulk-process-progress',
                duration: Infinity,
                dismissible: false,
              });
            }
          },
        });
        toast.dismiss('bulk-process-progress');
        if (runId !== runIdRef.current) return null;

        if (result.processed > 0) {
          toast.success(t('applications.bulkProcessSuccess', { count: result.processed }));
        }
        if (result.errors > 0) {
          toast.error(t('applications.bulkProcessPartial', { count: result.errors }));
        }
        if (result.skippedBilling > 0) {
          const remaining = Math.max(0, apps.length - result.processed - result.errors);
          toast.warning(t('application.pendingBilling.bulkAllSkipped', {
            count: remaining > 0 ? remaining : result.skippedBilling,
          }));
          clearQueueForIds(allIds);
          bulkTargetIdsRef.current = [];
          clearBulkBannerRun();
          if (companyId) clearBulkProcessQueue(companyId);
          emitBillingUsageChanged();
        }

        await refetchWithInflightCoalesce();
        setStoppingIds(prev => {
          const next = new Set(prev);
          for (const id of allIds) next.delete(id);
          return next.size === prev.size ? prev : next;
        });
        // Keep processingIds for successfully enqueued rows until pipelines settle.
        return result;
      } catch (err: unknown) {
        const msg = (err as { message?: string })?.message ?? 'Bulk process failed';
        toast.error(msg);
        clearQueueForIds(allIds);
        bulkTargetIdsRef.current = [];
        clearBulkBannerRun();
        if (companyId) clearBulkProcessQueue(companyId);
        await refetchWithInflightCoalesce();
        return null;
      }
    },
    [companyId, prepareBulkProcess, refetchWithInflightCoalesce, clearQueueForIds, clearBulkBannerRun, t],
  );

  const activeCount = useMemo(() => {
    let count = 0;
    for (const id of processingIds) {
      if (isProcessing(id)) count += 1;
    }
    for (const id of stoppingIds) {
      if (isStopping(id)) count += 1;
    }
    return count;
  }, [processingIds, stoppingIds, isProcessing, isStopping]);

  return {
    stoppingIds,
    processingIds,
    isStopping,
    isProcessing,
    isInProcessingFilter,
    showsActiveProcessingBadge,
    showsProcessingStuckBadge,
    showsQueuedInBulkBadge,
    activeCount,
    markBulkStop,
    markBulkProcess,
    prepareBulkProcess,
    runBulkAbortStuck,
    runBulkProcess,
    isExplicitStopEligible,
    countSelectedStopEligible,
  };
}
